import { QuestTriggerType } from "@playcharge/common/models/backend/quests/quest-trigger-type";
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, inject } from "@angular/core";
import { QuestTemplateStore } from "../../../../stores/quests/quest-template.store";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatOption, MatSelect } from "@angular/material/select";
import { MatButton } from "@angular/material/button";
import { EnumKeyValuePipe } from "../../../../pipes/enumKeyValue.pipe";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { QuestTemplateViewModel } from "@playcharge/common/models/backend/quests/backoffice/quest-template-view-model";
import { QuestConditionType } from "@playcharge/common/models/backend/quests/quest-condition-type";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { QuestTemplateEditModel } from "@playcharge/common/models/backend/quests/backoffice/quest-template-edit-model";
import { Observable, tap } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from "@angular/material/chips";
import { MatIcon } from "@angular/material/icon";
import { Router } from "@angular/router";
import { ComponentRoutes } from "../../../../models/constants/component-routes";
import { QuestConditionForm, QuestTemplateForm } from "../../../../models/quests/quest-form";
import { QuestBaseComponent } from "../../quest-base.component";
import { NoScrollDirective } from "../../../../directives/noScrollDirective";

@Component({
    selector: "app-quest-template",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatSelect,
        MatOption,
        MatChipGrid,
        MatChipRow,
        MatChipInput,
        MatChipRemove,
        MatIcon,
        MatButton,
        EnumKeyValuePipe,
        NoScrollDirective,
    ],
    providers: [QuestTemplateStore],
    templateUrl: "./quest-template.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestTemplateComponent extends QuestBaseComponent {
    private _templateStore: QuestTemplateStore = inject(QuestTemplateStore);
    private _router: Router = inject(Router);
    private _toastrService: ToastrService = inject(ToastrService);

    public form: FormGroup<QuestTemplateForm> = new FormGroup<QuestTemplateForm>({
        name: new FormControl<Nullable<string>>(null),
        triggerType: new FormControl<QuestTriggerType>(QuestTriggerType.Registered, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        scheduleId: new FormControl<Nullable<number>>(null),
        condition: new FormGroup<QuestConditionForm>({
            conditionType: new FormControl<QuestConditionType>(null!, {
                nonNullable: true,
                validators: [Validators.required],
            }),
            count: new FormControl<number>(0, {
                nonNullable: true,
                validators: [Validators.required],
            }),
            amount: new FormControl<number>(0, {
                nonNullable: true,
                validators: [Validators.required],
            }),
            goalIds: new FormControl<Nullable<number[]>>(null),
        }),
        offerId: new FormControl<Nullable<number>>(null),
        moneyReward: new FormControl<number>(0, {
            nonNullable: true,
            validators: [Validators.required],
        }),
        expReward: new FormControl<number>(0, {
            nonNullable: true,
            validators: [Validators.required],
        }),
    });

    constructor() {
        super();

        effect(() => this.templateChangeEffectFn());

        this.form.controls.triggerType.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe((value) => this.onTriggerTypeChanged(value));
    }

    public createOrUpdateTemplate(): void {
        const formValue = this.form.getRawValue();

        const model: QuestTemplateEditModel = {
            id: this._templateStore.templateId(),
            name: formValue.name,
            triggerType: formValue.triggerType,
            scheduleId: formValue.scheduleId,
            condition: formValue.condition,
            offerId: formValue.offerId,
            moneyReward: formValue.moneyReward,
            expReward: formValue.expReward,
        };

        let request: Observable<QuestTemplateViewModel>;

        if (this._templateStore.templateId() === 0) {
            request = this.templatesStore.createTemplate(model).pipe(
                tap((template) => {
                    this._router.navigateByUrl(
                        ComponentRoutes.quests.template.replace(
                            ":templateId",
                            template.id.toString(),
                        ),
                    );
                }),
            );
        } else {
            request = this.templatesStore.editTemplate(model);
        }

        request.subscribe({
            next: () => this._toastrService.success("Done"),
            error: (error) =>
                this._toastrService.error(error?.error?.description ?? "Internal Error"),
        });
    }

    public override get goalIdsControl(): FormControl<Nullable<number[]>> {
        return this.form.controls.condition.controls.goalIds;
    }

    private templateChangeEffectFn(): void {
        const template: Nullable<QuestTemplateViewModel> = this._templateStore.template();

        if (template) {
            this.form.patchValue(template);
        }
    }

    private onTriggerTypeChanged(value: QuestTriggerType): void {
        if (value !== QuestTriggerType.Schedule) {
            this.form.patchValue({ scheduleId: null });
        }
    }
}
